import React, { useEffect, useState } from "react";

import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PaymentsIcon from "@mui/icons-material/Payments";
import CreditScoreIcon from "@mui/icons-material/CreditScore";
import AgricultureIcon from "@mui/icons-material/Agriculture";
import BuildIcon from "@mui/icons-material/Build";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";

import { CRM_ModalContacto } from "./CRM_ModalContacto";
import { CRM_LeyendaAuditoria } from "./CRM_LeyendaAuditoria";
import { CRM_LeyendaDepartamento } from "./CRM_LeyendaDepartamento";

// tipo_contacto llega como texto completo en mayúsculas sin acentos (ej. "SERVICIO"), no como código
const TIPO_DISPLAY = { COBRANZA: "Cobranza", CREDITO: "Crédito", VENTAS: "Ventas", REFACCIONES: "Refacciones", SERVICIO: "Servicio" };

const TIPO_ICONO = {
  COBRANZA: <PaymentsIcon fontSize="small" />,
  CREDITO: <CreditScoreIcon fontSize="small" />,
  VENTAS: <AgricultureIcon fontSize="small" />,
  REFACCIONES: <BuildIcon fontSize="small" />,
  SERVICIO: <HeadsetMicIcon fontSize="small" />,
};

// Orden fijo de departamentos en la tabla pivotada, independiente del orden en que lleguen del API.
// "POSVENTAS" (código legado) se excluye a propósito — ya no aplica en este módulo.
const ORDEN_TIPOS = ["COBRANZA", "CREDITO", "VENTAS", "REFACCIONES", "SERVICIO"];
const TIPOS_EXCLUIDOS = ["POSVENTAS"];

// Agrupa el listado plano (una fila por medio+tipo, puede haber varias por el mismo medio) en
// una fila por tipo de contacto, con cada medio como lista de registros — permite varios
// correos/teléfonos/whatsapp del mismo tipo. Normaliza tipo_contacto/medio_contacto (trim +
// mayúsculas) por si el API los regresa con distinta capitalización o espacios.
const agruparPorTipo = (contactos) => {
  const grupos = {};
  const ordenEncontrado = [];

  contactos.forEach((c) => {
    const tipo = (c.tipo_contacto || "").trim().toUpperCase();
    if (!tipo || TIPOS_EXCLUIDOS.includes(tipo)) return;
    if (!grupos[tipo]) {
      grupos[tipo] = { tipo_contacto: tipo, comentarios: "", CORREO: [], TELEFONO: [], WHATSAPP: [] };
      ordenEncontrado.push(tipo);
    }
    const medio = (c.medio_contacto || "").trim().toUpperCase();
    if (medio in grupos[tipo]) grupos[tipo][medio].push(c);
    // El comentario se toma del primer registro del grupo que ya lo traiga capturado (es único por tipo)
    if (!grupos[tipo].comentarios && c.comentarios) grupos[tipo].comentarios = c.comentarios;
  });

  const tiposConocidos = ORDEN_TIPOS.filter((tipo) => grupos[tipo]);
  const tiposDesconocidos = ordenEncontrado.filter((tipo) => !ORDEN_TIPOS.includes(tipo));

  return [...tiposConocidos, ...tiposDesconocidos].map((tipo) => grupos[tipo]);
};

export const CRM_DetalleContactos = ({ esNuevo, idcliente, contactoCliente = [], soloLectura = false, responsableDepartamento = [] }) => {
  const [contactos, setContactos] = useState(contactoCliente);
  const [openModal, setOpenModal] = useState(false);
  const [filaEditando, setFilaEditando] = useState(null);

  // contactoCliente llega después del primer render (fetch async en CRM_DetalleScreen) — se sincroniza aquí
  useEffect(() => {
    setContactos(contactoCliente);
  }, [contactoCliente]);

  const handleAgregar = () => {
    setFilaEditando(null);
    setOpenModal(true);
  };

  const handleEditarFila = (fila) => {
    setFilaEditando(fila);
    setOpenModal(true);
  };

  // El modal regresa el listado ya actualizado desde el propio POST — se refleja directo
  const handleGuardado = (listadoActualizado) => {
    setContactos(listadoActualizado || []);
  };

  const filas = agruparPorTipo(contactos);

  return (
    <div id="sec-contacto" className="crm-detalle-card" style={{ scrollMarginTop: 170 }}>
      <div className="crm-detalle-card-header crm-detalle-card-header-actions">
        <div>
          <h3>Contactos</h3>
          <p>Personas de contacto registradas del cliente</p>
        </div>
        {!soloLectura && (
        <div className="crm-detalle-card-header-buttons">
          <button type="button" className="btn-primario-solido" onClick={handleAgregar}>
            <AddCircleOutlineIcon fontSize="small" /> Agregar contacto
          </button>
        </div>
        )}
      </div>

      {filas.length === 0 ? (
        <div className="crm-detalle-card-body">
          <p className="crm-sin-info">Aún no hay contactos registrados para este cliente.</p>
        </div>
      ) : (
        <div className="crm-detalle-card-body crm-detalle-tabla-wrap">
          <table className="crm-detalle-tabla">
            <thead>
              <tr>
                <th>Tipo de contacto</th>
                <th>Teléfono</th>
                <th>WhatsApp</th>
                <th>Correo</th>
                <th>Comentario</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => (
                <tr key={f.tipo_contacto}>
                  <td>
                    <div className="crm-detalle-contacto-cell">
                      <span className="contacto-avatar">{TIPO_ICONO[f.tipo_contacto]}</span>
                      <span>{TIPO_DISPLAY[f.tipo_contacto] || f.tipo_contacto}</span>
                    </div>
                  </td>
                  <td className="mono">
                    {f.TELEFONO.length > 0
                      ? f.TELEFONO.map((r) => (
                          <div key={r.orden} className="crm-detalle-medio-cell">
                            <span>{r.valor}</span>
                            {r.responsable && <span className="crm-detalle-medio-responsable">{r.responsable}</span>}
                          </div>
                        ))
                      : "-"}
                  </td>
                  <td className="mono">
                    {f.WHATSAPP.length > 0
                      ? f.WHATSAPP.map((r) => (
                          <div key={r.orden} className="crm-detalle-medio-cell">
                            <span>{r.valor}</span>
                            {r.responsable && <span className="crm-detalle-medio-responsable">{r.responsable}</span>}
                          </div>
                        ))
                      : "-"}
                  </td>
                  <td className="mono">
                    {f.CORREO.length > 0
                      ? f.CORREO.map((r) => (
                          <div key={r.orden} className="crm-detalle-medio-cell">
                            <span>{r.valor}</span>
                            {r.responsable && <span className="crm-detalle-medio-responsable">{r.responsable}</span>}
                          </div>
                        ))
                      : "-"}
                  </td>
                  <td>{f.comentarios || "-"}</td>
                  <td>
                    {!soloLectura && (
                    <button type="button" className="btn-row-menu" onClick={() => handleEditarFila(f)}>
                      <EditIcon fontSize="small" />
                    </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CRM_ModalContacto
        open={openModal}
        onClose={() => setOpenModal(false)}
        idcliente={idcliente}
        fila={filaEditando}
        filasExistentes={filas}
        onGuardado={handleGuardado}
      />

      <div className="crm-detalle-card-footer">
        <CRM_LeyendaDepartamento responsables={responsableDepartamento} idrel={8} />
        <CRM_LeyendaAuditoria auditoria={contactos[0]} />
      </div>
    </div>
  );
};
